import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatBRLInput, parseToFloat, formatFloatForInput } from '../utils/currencyUtils';
import api from '../services/api';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  roles: string[];
  lastLogin: string;
  totalStudyTime: number;
  cardsStudied: number;
  createdAt: string;
  subscriptionTier: 'free' | 'premium';
  subscriptionExpiresAt?: string;
  subscriptionValue?: number;
  subscriptionInterval?: 'month' | 'year';
  isSubscriptionActive: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionCancelled?: boolean;
  subscriptionCancelledAt?: string;
  // OAuth fields
  googleId?: string;
  facebookId?: string;
  appleId?: string;
  provider?: string;
  profilePicture?: string;
  // Email tokens (read-only)
  emailVerificationToken?: string;
  emailVerificationTokenExpires?: string;
  passwordResetToken?: string;
  passwordResetTokenExpires?: string;
  // Security
  loginAttempts?: number;
  lockUntil?: string;
  // Stats
  streakDays?: number;
  deckCount?: number;
  monthlyStudyTime?: number;
  monthlyCardsStudied?: number;
  usageResetDate?: string;
  // One-day pass
  oneDayPassActive?: boolean;
  oneDayPassExpiresAt?: string;
  oneDayPassPurchaseHistory?: string[];
  // Email preferences
  emailNotificationsEnabled?: boolean;
  reviewRemindersEnabled?: boolean;
  preferredReminderTime?: string;
  timezone?: string;
  lastReminderSent?: string;
  lastQuizReminderSent?: string;
  lastReengagementEmailSent?: string;
}

interface EmailTriggerButtonProps {
  label: string;
  description: string;
  emailType: string;
  userId: string;
}

const EmailTriggerButton: React.FC<EmailTriggerButtonProps> = ({ label, description, emailType, userId }) => {
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSendEmail = async () => {
    try {
      setSending(true);
      setStatus('idle');
      
      await api.post(`/admin/users/${userId}/send-email`, {
        emailType
      });
      
      setStatus('success');
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error: any) {
      console.error('Failed to send email:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-1.5">
        <div className="flex-1">
          <h3 className="text-xs font-medium text-gray-900 dark:text-white">{label}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        </div>
        <EnvelopeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2" />
      </div>
      
      <button
        onClick={handleSendEmail}
        disabled={sending}
        className={`w-full mt-2 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
          status === 'success'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
            : status === 'error'
            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700'
            : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-900/50'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {sending ? (
          <span className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-3 w-3 border-b border-current mr-2"></div>
            Sending...
          </span>
        ) : status === 'success' ? (
          '✓ Sent!'
        ) : status === 'error' ? (
          '✗ Failed'
        ) : (
          'Send Email'
        )}
      </button>
    </div>
  );
};


const AdminUserEdit: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'auth' | 'subscription' | 'usage' | 'emails' | 'info' | 'danger'>('basic');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    emailVerified: false,
    roles: [] as string[],
    subscriptionTier: 'free' as 'free' | 'premium',
    subscriptionExpiresAt: '',
    subscriptionValue: 0.0,
    subscriptionInterval: '' as 'month' | 'year' | '',
    stripeCustomerId: '',
    stripeSubscriptionId: '',
    subscriptionCancelled: false,
    subscriptionCancelledAt: '',
    isSubscriptionActive: false,
    // OAuth fields
    googleId: '',
    facebookId: '',
    appleId: '',
    provider: 'local',
    profilePicture: '',
    // Stats
    streakDays: 0,
    deckCount: 0,
    monthlyStudyTime: 0,
    monthlyCardsStudied: 0,
    // One-day pass
    oneDayPassActive: false,
    oneDayPassExpiresAt: '',
    // Email preferences
    emailNotificationsEnabled: true,
    reviewRemindersEnabled: true,
    preferredReminderTime: '08:00',
    timezone: 'UTC',
  });

  // Display state for subscription value (Brazilian format)
  const [subscriptionValueDisplay, setSubscriptionValueDisplay] = useState('0,00');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin');
      return;
    }

    if (userId) {
      fetchUser();
    }
  }, [userId, isAdmin, navigate]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/admin/users/${userId}`);
      const userData = response.data;
      setUser(userData);
      
      // Initialize form data
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        emailVerified: userData.emailVerified || false,
        roles: userData.roles || [],
        subscriptionTier: userData.subscriptionTier || 'free',
        subscriptionExpiresAt: userData.subscriptionExpiresAt 
          ? new Date(userData.subscriptionExpiresAt).toISOString().slice(0, 16)
          : '',
        subscriptionValue: userData.subscriptionValue || 0.0,
        subscriptionInterval: userData.subscriptionInterval || '',
        stripeCustomerId: userData.stripeCustomerId || '',
        stripeSubscriptionId: userData.stripeSubscriptionId || '',
        subscriptionCancelled: userData.subscriptionCancelled || false,
        subscriptionCancelledAt: userData.subscriptionCancelledAt 
          ? new Date(userData.subscriptionCancelledAt).toISOString().slice(0, 16)
          : '',
        isSubscriptionActive: userData.isSubscriptionActive || false,
        // OAuth fields
        googleId: userData.googleId || '',
        facebookId: userData.facebookId || '',
        appleId: userData.appleId || '',
        provider: userData.provider || 'local',
        profilePicture: userData.profilePicture || '',
        // Stats
        streakDays: userData.streakDays || 0,
        deckCount: userData.deckCount || 0,
        monthlyStudyTime: userData.monthlyStudyTime || 0,
        monthlyCardsStudied: userData.monthlyCardsStudied || 0,
        // One-day pass
        oneDayPassActive: userData.oneDayPassActive || false,
        oneDayPassExpiresAt: userData.oneDayPassExpiresAt 
          ? new Date(userData.oneDayPassExpiresAt).toISOString().slice(0, 16)
          : '',
        // Email preferences
        emailNotificationsEnabled: userData.emailNotificationsEnabled !== undefined ? userData.emailNotificationsEnabled : true,
        reviewRemindersEnabled: userData.reviewRemindersEnabled !== undefined ? userData.reviewRemindersEnabled : true,
        preferredReminderTime: userData.preferredReminderTime || '08:00',
        timezone: userData.timezone || 'UTC',
      });
      
      // Set display value for subscription
      setSubscriptionValueDisplay(formatFloatForInput(userData.subscriptionValue || 0.0));
    } catch (error: any) {
      console.error('Failed to fetch user:', error);
      setError(error.response?.data?.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'subscriptionValue') {
      // Handle Brazilian format input
      const formattedValue = formatBRLInput(value);
      setSubscriptionValueDisplay(formattedValue);
      
      // Parse to float for internal storage
      const numericValue = parseToFloat(formattedValue);
      setFormData(prev => ({
        ...prev,
        subscriptionValue: numericValue
      }));
    } else if (name === 'subscriptionTier') {
      // Automatically set isSubscriptionActive based on tier
      setFormData(prev => ({
        ...prev,
        subscriptionTier: value as 'free' | 'premium',
        isSubscriptionActive: value === 'premium'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    try {
      setSaving(true);
      setError(null);

      // Update user basic info
      await api.put(`/admin/users/${userId}`, {
        name: formData.name,
        email: formData.email,
        emailVerified: formData.emailVerified,
        stripeCustomerId: formData.stripeCustomerId,
        stripeSubscriptionId: formData.stripeSubscriptionId,
        subscriptionCancelled: formData.subscriptionCancelled,
        subscriptionCancelledAt: formData.subscriptionCancelledAt || null,
        // OAuth fields
        googleId: formData.googleId,
        facebookId: formData.facebookId,
        appleId: formData.appleId,
        provider: formData.provider,
        profilePicture: formData.profilePicture,
        // Stats
        streakDays: formData.streakDays,
        deckCount: formData.deckCount,
        monthlyStudyTime: formData.monthlyStudyTime,
        monthlyCardsStudied: formData.monthlyCardsStudied,
        // One-day pass
        oneDayPassActive: formData.oneDayPassActive,
        oneDayPassExpiresAt: formData.oneDayPassExpiresAt || null,
        // Email preferences
        emailNotificationsEnabled: formData.emailNotificationsEnabled,
        reviewRemindersEnabled: formData.reviewRemindersEnabled,
        preferredReminderTime: formData.preferredReminderTime,
        timezone: formData.timezone,
      });

      // Update roles
      await api.post(`/admin/users/${userId}/roles`, {
        roles: formData.roles
      });

      // Update subscription if tier, value, or active status changed
      const subscriptionChanged = formData.subscriptionTier !== user?.subscriptionTier ||
                                  formData.subscriptionValue !== user?.subscriptionValue ||
                                  formData.isSubscriptionActive !== user?.isSubscriptionActive ||
                                  (formData.subscriptionTier === 'premium' && formData.subscriptionExpiresAt !== 
                                   (user?.subscriptionExpiresAt ? new Date(user.subscriptionExpiresAt).toISOString().slice(0, 16) : ''));

      console.log('Subscription change check:', {
        formTier: formData.subscriptionTier,
        userTier: user?.subscriptionTier,
        formValue: formData.subscriptionValue,
        userValue: user?.subscriptionValue,
        formActive: formData.isSubscriptionActive,
        userActive: user?.isSubscriptionActive,
        subscriptionChanged
      });

      if (subscriptionChanged) {
        const subscriptionData: any = {
          tier: formData.subscriptionTier,
          isSubscriptionActive: formData.isSubscriptionActive
        };

        if (formData.subscriptionTier === 'premium' && formData.subscriptionExpiresAt) {
          subscriptionData.subscriptionExpiresAt = new Date(formData.subscriptionExpiresAt).toISOString();
        }

        // Always send subscription value and interval for premium subscriptions
        if (formData.subscriptionTier === 'premium') {
          subscriptionData.subscriptionValue = formData.subscriptionValue;
          if (formData.subscriptionInterval) {
            subscriptionData.subscriptionInterval = formData.subscriptionInterval;
          }
        }

        console.log('Sending subscription update:', subscriptionData);

        await api.post(`/admin/users/${userId}/subscription`, subscriptionData);
      }

      // Success - redirect back to admin
      navigate('/admin');
    } catch (error: any) {
      console.error('Failed to update user:', error);
      setError(error.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userId || !user) return;

    const confirmMessage = `Are you sure you want to delete user "${user.name}" (${user.email})? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      await api.delete(`/admin/users/${userId}`);
      
      // Success - redirect back to admin
      navigate('/admin');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      setError(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Edit User</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Modify user information and permissions
          </p>
        </div>
        <Link
          to="/admin"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Admin
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Error updating user
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'basic'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Basic Info
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('auth')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'auth'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Authentication
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('subscription')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'subscription'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Subscription
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('usage')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'usage'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Usage & Stats
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('emails')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'emails'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Email & Notifications
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('info')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'info'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Read-Only Info
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('danger')}
              className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === 'danger'
                  ? 'border-red-500 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300'
              }`}
            >
              Danger Zone
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>
              <div>
                <label htmlFor="provider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Provider
                </label>
                <select
                  id="provider"
                  name="provider"
                  value={formData.provider}
                  onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="local">Local</option>
                  <option value="google">Google</option>
                  <option value="facebook">Facebook</option>
                  <option value="apple">Apple</option>
                </select>
              </div>
              <div>
                <label htmlFor="profilePicture" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Profile Picture URL
                </label>
                <input
                  type="text"
                  id="profilePicture"
                  name="profilePicture"
                  value={formData.profilePicture}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="emailVerified"
                  checked={formData.emailVerified}
                  onChange={handleInputChange}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Email Verified</span>
              </label>
            </div>
          </div>

          {/* Roles */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Roles</h2>
            <div className="space-y-3">
              {['user', 'moderator', 'admin'].map((role) => (
                <label key={role} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role)}
                    onChange={(e) => handleRoleChange(role, e.target.checked)}
                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="capitalize text-sm text-gray-700 dark:text-gray-300">{role}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

          {/* Authentication Tab */}
          {activeTab === 'auth' && (
            <div className="space-y-6">
              {/* OAuth IDs */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">OAuth IDs</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="googleId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Google ID
                </label>
                <input
                  type="text"
                  id="googleId"
                  name="googleId"
                  value={formData.googleId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Google OAuth ID"
                />
              </div>
              <div>
                <label htmlFor="facebookId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Facebook ID
                </label>
                <input
                  type="text"
                  id="facebookId"
                  name="facebookId"
                  value={formData.facebookId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Facebook OAuth ID"
                />
              </div>
              <div>
                <label htmlFor="appleId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Apple ID
                </label>
                <input
                  type="text"
                  id="appleId"
                  name="appleId"
                  value={formData.appleId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Apple OAuth ID"
                />
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="space-y-6">
              {/* Subscription */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Subscription</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="stripeCustomerId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stripe Customer ID
                  </label>
                  <input
                    type="text"
                    id="stripeCustomerId"
                    name="stripeCustomerId"
                    value={formData.stripeCustomerId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    placeholder="cus_..."
                  />
                </div>
                <div>
                  <label htmlFor="stripeSubscriptionId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Stripe Subscription ID
                  </label>
                  <input
                    type="text"
                    id="stripeSubscriptionId"
                    name="stripeSubscriptionId"
                    value={formData.stripeSubscriptionId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    placeholder="sub_..."
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subscription Tier
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="subscriptionTier"
                        value="free"
                        checked={formData.subscriptionTier === 'free'}
                        onChange={handleInputChange}
                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Free</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="subscriptionTier"
                        value="premium"
                        checked={formData.subscriptionTier === 'premium'}
                        onChange={handleInputChange}
                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Premium</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subscription Status
                  </label>
                  <label className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      name="isSubscriptionActive"
                      checked={formData.isSubscriptionActive}
                      onChange={handleInputChange}
                      className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Is Subscription Active</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Automatically set based on tier, but can be manually overridden
                  </p>
                </div>
              </div>

              {formData.subscriptionTier === 'premium' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="subscriptionExpiresAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Expiration Date
                      </label>
                      <input
                        type="datetime-local"
                        id="subscriptionExpiresAt"
                        name="subscriptionExpiresAt"
                        value={formData.subscriptionExpiresAt}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                    <div>
                      <label htmlFor="subscriptionInterval" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Billing Interval
                      </label>
                      <select
                        id="subscriptionInterval"
                        name="subscriptionInterval"
                        value={formData.subscriptionInterval}
                        onChange={(e) => setFormData(prev => ({ ...prev, subscriptionInterval: e.target.value as 'month' | 'year' | '' }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">None</option>
                        <option value="month">Monthly</option>
                        <option value="year">Yearly</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subscriptionValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Valor da Assinatura (BRL)
                    </label>
                    <input
                      type="text"
                      id="subscriptionValue"
                      name="subscriptionValue"
                      value={subscriptionValueDisplay}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      placeholder="79,90"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Use vírgula como separador decimal (ex: 79,90)
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="subscriptionCancelled"
                          checked={formData.subscriptionCancelled}
                          onChange={handleInputChange}
                          className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Subscription Cancelled</span>
                      </label>
                    </div>
                    {formData.subscriptionCancelled && (
                      <div>
                        <label htmlFor="subscriptionCancelledAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Cancelled At
                        </label>
                        <input
                          type="datetime-local"
                          id="subscriptionCancelledAt"
                          name="subscriptionCancelledAt"
                          value={formData.subscriptionCancelledAt}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* One-Day Pass */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">One-Day Pass</h2>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="oneDayPassActive"
                    checked={formData.oneDayPassActive}
                    onChange={handleInputChange}
                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">One-Day Pass Active</span>
                </label>
              </div>
              {formData.oneDayPassActive && (
                <div>
                  <label htmlFor="oneDayPassExpiresAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Pass Expires At
                  </label>
                  <input
                    type="datetime-local"
                    id="oneDayPassExpiresAt"
                    name="oneDayPassExpiresAt"
                    value={formData.oneDayPassExpiresAt}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              )}
              {user && user.oneDayPassPurchaseHistory && user.oneDayPassPurchaseHistory.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Purchase History</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    {user.oneDayPassPurchaseHistory.map((date, idx) => (
                      <div key={idx}>{new Date(date).toLocaleString()}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

          {/* Usage & Stats Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              {/* User Stats */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Usage & Stats</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="streakDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Streak Days
                </label>
                <input
                  type="number"
                  id="streakDays"
                  name="streakDays"
                  value={formData.streakDays}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  min="0"
                />
              </div>
              <div>
                <label htmlFor="deckCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Deck Count
                </label>
                <input
                  type="number"
                  id="deckCount"
                  name="deckCount"
                  value={formData.deckCount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  min="0"
                />
              </div>
              <div>
                <label htmlFor="monthlyStudyTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly Study Time (seconds)
                </label>
                <input
                  type="number"
                  id="monthlyStudyTime"
                  name="monthlyStudyTime"
                  value={formData.monthlyStudyTime}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  min="0"
                />
              </div>
              <div>
                <label htmlFor="monthlyCardsStudied" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly Cards Studied
                </label>
                <input
                  type="number"
                  id="monthlyCardsStudied"
                  name="monthlyCardsStudied"
                  value={formData.monthlyCardsStudied}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Email & Notifications Tab */}
          {activeTab === 'emails' && (
            <div className="space-y-6">
              {/* Email Preferences */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Email Preferences</h2>
            <div className="space-y-4">
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="emailNotificationsEnabled"
                    checked={formData.emailNotificationsEnabled}
                    onChange={handleInputChange}
                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Email Notifications Enabled</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="reviewRemindersEnabled"
                    checked={formData.reviewRemindersEnabled}
                    onChange={handleInputChange}
                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Review Reminders Enabled</span>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="preferredReminderTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Preferred Reminder Time (HH:mm)
                  </label>
                  <input
                    type="time"
                    id="preferredReminderTime"
                    name="preferredReminderTime"
                    value={formData.preferredReminderTime}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Timezone
                  </label>
                  <input
                    type="text"
                    id="timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                    placeholder="UTC, America/Sao_Paulo, etc."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Email Triggers */}
          {user && (
            <div className="border-t border-gray-200 dark:border-gray-600 pt-6 mt-6">
              <h2 className="text-base font-medium text-gray-900 dark:text-white mb-2">Email Triggers</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Send test emails to this user. These emails will be sent immediately.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <EmailTriggerButton
                  label="Welcome Email"
                  description="Welcome message for new users"
                  emailType="welcome"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Email Verification"
                  description="Email address verification link"
                  emailType="verification"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Password Reset"
                  description="Password reset instructions"
                  emailType="password-reset"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Security Alert"
                  description="Security notification"
                  emailType="security-alert"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Combined Review Reminder"
                  description="Reminder for cards and quizzes review"
                  emailType="combined-review-reminder"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Inactive User"
                  description="Re-engagement for inactive users"
                  emailType="inactive-user"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Payment Failed"
                  description="Subscription payment failure notification"
                  emailType="payment-failed"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Streak Freeze Warning"
                  description="Warning for days 1-2 of inactivity (freeze protection)"
                  emailType="streak-freeze-warning"
                  userId={user?._id || ''}
                />
                <EmailTriggerButton
                  label="Streak Loss Imminent"
                  description="Critical warning for day 3 of inactivity"
                  emailType="streak-loss-imminent"
                  userId={user?._id || ''}
                />
              </div>
            </div>
          )}
        </div>
      )}

          {/* Read-Only Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Read-only Information */}
              {user && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Read-Only Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Cards Studied</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{user?.cardsStudied}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Study Time</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{user?.totalStudyTime}min</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Login Attempts</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">{user?.loginAttempts || 0}</div>
                    </div>
                    {user?.lockUntil && (
                      <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded-md">
                        <div className="text-sm font-medium text-red-700 dark:text-red-300">Locked Until</div>
                        <div className="text-lg font-semibold text-red-900 dark:text-red-200">
                          {new Date(user.lockUntil).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {user?.usageResetDate && (
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Usage Reset Date</div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {new Date(user.usageResetDate).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                    {user?.lastReminderSent && (
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Reminder</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(user.lastReminderSent).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {user?.lastQuizReminderSent && (
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Quiz Reminder</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(user.lastQuizReminderSent).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {user?.lastReengagementEmailSent && (
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Reengagement Email</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(user.lastReengagementEmailSent).toLocaleString()}
                        </div>
                      </div>
                    )}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {user?.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Subscription Active</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {user?.isSubscriptionActive ? '✓ Yes' : '✗ No'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-red-900 dark:text-red-200 mb-4">Danger Zone</h2>
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-medium text-red-900 dark:text-red-200">Delete User Account</h3>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                    Permanently delete this user account and all associated data. This action cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="ml-4 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2 inline-block"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Submit Button - Always visible */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600 mt-8">
            <Link
              to="/admin"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2 inline-block"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUserEdit;